import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { object, string, number } from "yup";
import { useHistory } from "react-router-dom";
import AdminFormModal from "../../AdminFormModal";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";
import { ReactComponent as ObjectiveIcon } from "../../../assets/images/sub-objectives-icon.svg";
import { updateSubObjective } from "../../../reducers/subStrategicObjective";
import { addToast } from "../../../helpers";
import api from "../../../providers/api";

import "./style.scss";

const { Field } = Form;

const validationSchema = object().shape({
  EnglishName: string().required(" "),
  ArabicName: string().required(" "),
  EnglishDescription: string().required(" "),
  ArabicDescription: string().required(" "),
  Code: string().required(" "),
  Order: number().required(" ").min(1, " "),
  Strategy: object().nullable().required(" "),
  Perspective: object().nullable().required(" "),
  StrategicObjective: object().nullable().required(" "),
});

const ObjectiveForm = ({
  data,
  closeModal,
  selectedStrategy,
  selectedPerspective,
  selectedObjective,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const [loading, setLoading] = useState(false);
  const [strategiesLoading, setStrategiesLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);

  const [initialValues, setInitialValues] = useState({
    EnglishName: "",
    ArabicName: "",
    EnglishDescription: "",
    ArabicDescription: "",
    Strategy: selectedStrategy,
    Perspective: selectedPerspective,
    StrategicObjective: selectedObjective,
    Order: "",
    Theme: null,
    OwnerModel: null,
    Code: "",
  });

  const getObjectiveData = () => {
    setLoading(true);
    api.subSo
      .getById(data?.ID)
      .then((res) => {
        setInitialValues({
          ...res.Data,
          Strategy: selectedStrategy,
          Perspective: selectedPerspective,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getStrategies = async () => {
    setStrategiesLoading(true);
    try {
      const res = await api.strategy.getAllWithObjectives();
      setStrategies(res.Data || []);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setStrategiesLoading(false);
    }
  };

  useEffect(() => {
    getStrategies();
    if (data) {
      getObjectiveData();
    }
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const body = {
        ...values,
      };
      const service = data ? "update" : "create";
      const res = await api.subSo[service](body);
      const { Data, StatusCode } = res;
      if (StatusCode === "Success") {
        dispatch(updateSubObjective(Data));
        addToast.success(data ? "toast.successUpdate" : "toast.successSave");
        closeModal();
      }
    } catch (error) {
      // addToast.error(error?.message, {
      //   toastId: "get-users-error",
      // });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({ id: "common.sub-strategic-objective" })}
      Icon={ObjectiveIcon}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="objective-form-body"
            >
              <Space
                direction="vertical"
                size="medium"
                className="form-container"
              >
                <Row size="small">
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "subObjective.english-title",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "subObjective.english-title",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({
                      id: "subObjective.arabic-title",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "subObjective.arabic-title",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="textarea"
                    name="EnglishDescription"
                    label={intl.formatMessage({
                      id: "subObjective.english-description",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "subObjective.english-description",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    type="textarea"
                    name="ArabicDescription"
                    label={intl.formatMessage({
                      id: "subObjective.arabic-description",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "subObjective.arabic-description",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row>
                  <Field
                    type="select"
                    name="Strategy"
                    label={intl.formatMessage({ id: "common.strategy" })}
                    inputProps={{
                      options: strategies,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el.ID,
                      isLoading: strategiesLoading,
                      placeholder: intl.formatMessage({
                        id: "common.strategy",
                      }),
                      onChange: (e) => {
                        setFieldValue("Strategy", e);
                        setFieldValue("Perspective", null);
                        setFieldValue("StrategicObjective", null);
                      },
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="select"
                    name="Perspective"
                    label={intl.formatMessage({ id: "common.perspective" })}
                    inputProps={{
                      options: values.Strategy?.Perspectives,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el.ID,
                      placeholder: intl.formatMessage({
                        id: "common.perspective",
                      }),
                      onChange: (e) => {
                        setFieldValue("Perspective", e);
                        setFieldValue("StrategicObjective", null);
                      },
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    name="StrategicObjective"
                    label={intl.formatMessage({
                      id: "common.strategic-objective",
                    })}
                    inputProps={{
                      options: values.Perspective?.StrategicObjectives,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el.ID,
                      placeholder: intl.formatMessage({
                        id: "common.strategic-objective",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="number"
                    name="Order"
                    label={intl.formatMessage({ id: "common.order" })}
                    inputProps={{
                      onKeyDown: (evt) => {
                        if (
                          evt.key === "e" ||
                          evt.key === "-" ||
                          evt.key === "+"
                        ) {
                          evt.preventDefault();
                        }
                      },
                      placeholder: intl.formatMessage({
                        id: "common.order",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    name="Code"
                    label={intl.formatMessage({ id: "common.code" })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "common.code",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add",
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default ObjectiveForm;
